import './InputField.scss';
import { Input, InputProps, Form, Tooltip } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import React, { ReactNode } from 'react';
import { Controller, UseControllerProps } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Block, FlexBox, Spacer } from '../../layouts';
import { TextCommon } from '../../typo';
import { FormConnector } from '../FormConnector';
import { AppButton } from '../../buttons';
import { classNames } from '../../../utils/class-names';

interface InputFieldProps {
    readonly type?: InputProps['type'];
    readonly name: string;
    readonly label?: string;
    readonly placeholder?: string;
    readonly rules?: UseControllerProps['rules'];
    readonly addonBefore?: ReactNode;
    readonly addonAfter?: ReactNode;
    readonly prefix?: ReactNode;
    readonly suffix?: ReactNode;
    readonly tooltipContent?: string;
    readonly onChange?: InputProps['onChange'];
    readonly onBlur?: InputProps['onBlur'];
    readonly disabled?: boolean;
    readonly description?: string;
    readonly maxLength?: number;
}

export const InputField = (props: React.PropsWithChildren<InputFieldProps>) => {
    const { t } = useTranslation();
    const [isFocused, setIsFocused] = React.useState(false);
    const [isHover, setIsHover] = React.useState(false);
    const { label, name, placeholder, type, rules, addonBefore, addonAfter, tooltipContent } = props;

    const onRender = React.useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (renderProps: any) => {
            const { onChange, value } = renderProps.field;
            const { error } = renderProps.fieldState;
            const Component = type === 'password' ? Input.Password : Input;

            const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
                setIsFocused(false);
                const trimmedValue = trimText(e.target.value);
                onChange(trimmedValue);
                props?.onBlur?.(e);
            };

            const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                onChange(e);
                props?.onChange?.(e);
            };

            const trimText = (input: string) => {
                return input.trim();
            };

            return (
                <Form.Item
                    validateStatus={error ? 'error' : undefined}
                    help={error?.message && t(error?.message)}
                >
                    <FlexBox className='app-input-field'>
                        <Component
                            prefix={props.prefix}
                            suffix={props.suffix}
                            disabled={props.disabled}
                            addonBefore={addonBefore != null
                                ? <span
                                    onMouseOver={() => setIsHover(true)}
                                    onMouseLeave={() => setIsHover(false)}
                                >{addonBefore}</span> : undefined}
                            addonAfter={addonAfter != null
                                ? <span
                                    onMouseOver={() => setIsHover(true)}
                                    onMouseLeave={() => setIsHover(false)}
                                >{addonAfter}</span> : undefined}
                            name={name}
                            value={value}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            className={classNames({
                                'ant-input-focused': isFocused,
                                'ant-input-hover': isHover,
                            })}
                            onFocus={() => setIsFocused(true)}
                            onMouseOver={() => setIsHover(true)}
                            onMouseLeave={() => setIsHover(false)}
                            placeholder={placeholder && t(placeholder)}
                            maxLength={props.maxLength}
                        />
                    </FlexBox>
                </Form.Item>
            );
        },
        [addonAfter, addonBefore, isFocused, isHover, name, placeholder, props, t, type]
    );

    const isRequired = !!rules?.required;
    return (
        <FormConnector>
            {(form) => {
                const { control } = form;
                return (
                    <Block width='100%'>
                        {
                            label && <Spacer bottom={6}>
                                <TextCommon
                                    fontWeight={600}
                                    lineHeight='20px'
                                    color='#344054'
                                >
                                    {t(label)}
                                </TextCommon>
                                {isRequired && (
                                    <span style={{ color: 'red' }}> *</span>
                                )}
                                {tooltipContent && <Tooltip placement='top' title={<span>{t(tooltipContent)}</span>}>
                                    <AppButton type='text' icon={<QuestionCircleOutlined />} />
                                </Tooltip>}
                            </Spacer>
                        }
                        {props.description && (<Spacer bottom={4}><TextCommon>{t(props.description)}</TextCommon></Spacer>)}
                        <Controller
                            name={name}
                            control={control}
                            render={onRender}
                            rules={rules}
                        />
                    </Block>
                );
            }}
        </FormConnector>
    );
};
